import React, { useState, useMemo } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useLocationContext } from '../hooks/useLocationContext';
import { useToast } from '../components/Toast';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import { ScopedLink } from '../components/ScopedLink';
import StaffComplianceNudge from '../components/staff-compliance/StaffComplianceNudge';
import {
  useGetSuperAdminsQuery,
  useAddSuperAdminMutation,
  useRemoveSuperAdminMutation,
  useGetOrgMembersQuery,
  useAddOrgMemberMutation,
  useRemoveOrgMemberMutation,
  useGetLocationMembersQuery,
  useAddLocationMemberMutation,
  useUpdateLocationMemberRoleMutation,
  useRemoveLocationMemberMutation,
  useResendAdminInviteMutation,
  useResendOrgMemberInviteMutation,
  useResendLocationMemberInviteMutation,
  useGetClassesQuery,
  useGetStaffClassesQuery,
  useSetClassStaffMutation,
  useMfaAdminResetMutation,
  localApi,
} from '../store/api/localApi';
import { useAppDispatch } from '../hooks/redux';
import { UserGroupIcon, PlusIcon } from '@heroicons/react/24/outline';

interface TeamMember {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  locationId?: string;
  locationName?: string;
  status: string;
  isPendingInvite?: boolean;
}

const ROLE_BADGE: Record<string, string> = {
  SUPER_ADMIN: 'bg-purple-100 text-purple-800',
  ORG_ADMIN: 'bg-blue-100 text-blue-800',
  LOCATION_ADMIN: 'bg-green-100 text-green-800',
  STAFF: 'bg-amber-100 text-amber-800',
};

const ROLE_LABELS: Record<string, string> = {
  SUPER_ADMIN: 'Super Admin',
  ORG_ADMIN: 'Org Admin',
  LOCATION_ADMIN: 'Location Admin',
  STAFF: 'Staff',
};

const STATUS_BADGE: Record<string, string> = {
  active: 'bg-green-100 text-green-800',
  invited: 'bg-yellow-100 text-yellow-800',
  pending: 'bg-yellow-100 text-yellow-800',
};

type TeamLevel = 'super_admin' | 'org' | 'location';

// ========== Invite Modal ==========
const InviteModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  level: TeamLevel;
  organizationId: string;
  locationId?: string;
  currentLocationName?: string;
  callerRole: string;
}> = ({ isOpen, onClose, level, organizationId, locationId, currentLocationName, callerRole }) => {
  const { showSuccess, showError } = useToast();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState(
    level === 'super_admin' ? 'SUPER_ADMIN' : level === 'org' ? 'ORG_ADMIN' : 'STAFF'
  );
  const [selectedClassIds, setSelectedClassIds] = useState<string[]>([]);

  const [addSuperAdmin, { isLoading: superLoading }] = useAddSuperAdminMutation();
  const [addOrgMember, { isLoading: orgLoading }] = useAddOrgMemberMutation();
  const [addLocationMember, { isLoading: locLoading }] = useAddLocationMemberMutation();
  const isLoading = superLoading || orgLoading || locLoading;

  // Load classes at this location for the multi-select. Only relevant for STAFF
  // invites at the location level — admins don't get class-scoped.
  const { data: classesForLocation } = useGetClassesQuery(locationId || '', {
    skip: !(level === 'location' && role === 'STAFF' && !!locationId),
  });

  const roleOptions = useMemo(() => {
    if (level === 'super_admin') {
      return [{ value: 'SUPER_ADMIN', label: 'Super Admin' }];
    }
    if (level === 'org') {
      return [{ value: 'ORG_ADMIN', label: 'Org Admin' }];
    }
    // Location level
    const options: { value: string; label: string }[] = [
      { value: 'STAFF', label: 'Staff' },
      { value: 'LOCATION_ADMIN', label: 'Location Admin' },
    ];
    return options;
  }, [level]);

  const resetForm = () => {
    setEmail('');
    setRole(level === 'super_admin' ? 'SUPER_ADMIN' : level === 'org' ? 'ORG_ADMIN' : 'STAFF');
    setSelectedClassIds([]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (level === 'super_admin') {
        await addSuperAdmin({ email }).unwrap();
      } else if (level === 'org') {
        await addOrgMember({ orgId: organizationId, email, role: 'ORG_ADMIN' }).unwrap();
      } else {
        await addLocationMember({
          orgId: organizationId,
          locationId: locationId || '',
          email,
          role,
          // Only send classIds for STAFF — backend ignores it for LOCATION_ADMIN.
          classIds: role === 'STAFF' ? selectedClassIds : undefined,
        }).unwrap();
      }
      const noClassWarning =
        role === 'STAFF' && selectedClassIds.length === 0
          ? ' (no classes assigned — admin can edit later)'
          : '';
      showSuccess(`Invitation sent to ${email}${noClassWarning}`);
      resetForm();
      onClose();
    } catch (err: any) {
      showError(err.data?.error || 'Failed to send invitation');
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Invite Team Member"
      testId="invite-member-modal"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="invite-email" className="block text-sm font-medium text-gray-700">
            Email
          </label>
          <input
            id="invite-email"
            type="email"
            required
            data-testid="invite-email-input"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        {level === 'location' && (
          <>
            <div>
              <label htmlFor="invite-role" className="block text-sm font-medium text-gray-700">
                Role
              </label>
              <select
                id="invite-role"
                data-testid="invite-role-select"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                value={role}
                onChange={(e) => setRole(e.target.value)}
              >
                {roleOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            {currentLocationName && (
              <div>
                <label
                  htmlFor="invite-location"
                  className="block text-sm font-medium text-gray-700"
                >
                  Location
                </label>
                <input
                  type="text"
                  data-testid="invite-location-select"
                  className="mt-1 block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm sm:text-sm"
                  value={currentLocationName}
                  disabled
                />
              </div>
            )}
            {role === 'STAFF' && (
              <div data-testid="invite-class-assignments">
                <label className="block text-sm font-medium text-gray-700">
                  Assigned Classes
                  <span className="ml-1 text-xs font-normal text-gray-500">
                    (optional — admin can edit later)
                  </span>
                </label>
                <div className="mt-1 max-h-40 overflow-y-auto rounded-md border border-gray-300 p-2 space-y-1">
                  {(classesForLocation || []).map((cls: any) => (
                    <label
                      key={cls.id}
                      className="flex items-center gap-2 text-sm py-0.5 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        data-testid={`invite-class-${cls.id}`}
                        checked={selectedClassIds.includes(cls.id)}
                        onChange={(e) => {
                          setSelectedClassIds((prev) =>
                            e.target.checked
                              ? [...prev, cls.id]
                              : prev.filter((id) => id !== cls.id)
                          );
                        }}
                      />
                      <span>{cls.name}</span>
                    </label>
                  ))}
                  {(!classesForLocation || classesForLocation.length === 0) && (
                    <p className="text-xs text-gray-500">
                      No classes available at this location yet.
                    </p>
                  )}
                </div>
                {selectedClassIds.length === 0 && (
                  <p className="mt-1 text-xs text-amber-600" data-testid="invite-class-warning">
                    Without class assignments this staff member won't see any students or attendance
                    until you assign them.
                  </p>
                )}
              </div>
            )}
          </>
        )}
        {level !== 'location' && roleOptions.length > 0 && (
          <div>
            <label htmlFor="invite-role" className="block text-sm font-medium text-gray-700">
              Role
            </label>
            <select
              id="invite-role"
              data-testid="invite-role-select"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              disabled={roleOptions.length === 1}
            >
              {roleOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading}
            data-testid="invite-submit-btn"
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-50"
          >
            {isLoading ? 'Sending...' : 'Send Invite'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

// ========== Edit Class Assignments Modal (staff at location level) ==========
const EditClassAssignmentsModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  member: TeamMember | null;
  locationId: string;
}> = ({ isOpen, onClose, member, locationId }) => {
  const { showSuccess, showError } = useToast();
  const dispatch = useAppDispatch();
  const { data: classesAtLocation } = useGetClassesQuery(locationId, {
    skip: !locationId || !isOpen,
  });
  const { data: staffClasses } = useGetStaffClassesQuery(
    { locationId, staffId: member?.userId || '' },
    { skip: !member || !isOpen }
  );
  const [setStaffOnClass, { isLoading }] = useSetClassStaffMutation();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Sync state when modal opens for a member.
  React.useEffect(() => {
    if (!staffClasses?.data) return;
    setSelectedIds(new Set(staffClasses.data.map((c: any) => c.id)));
  }, [staffClasses, member?.userId]);

  if (!member) return null;

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!classesAtLocation) return;
    const wanted = selectedIds;
    const current = new Set((staffClasses?.data || []).map((c: any) => c.id));
    try {
      // For each class at the location, set its staff list including/excluding this user.
      // We do per-class PUTs because the existing endpoint is class-scoped, and
      // these tend to be small lists. Optimal-batch could be added later.
      const ops: Promise<unknown>[] = [];
      for (const cls of classesAtLocation) {
        const wantsThis = wanted.has(cls.id);
        const hasThis = current.has(cls.id);
        if (wantsThis === hasThis) continue;
        // PUT /classes/:id/staff replaces the whole staff list, so we read
        // the current set first to avoid clobbering other staff.
        ops.push(
          (async () => {
            const body = await dispatch(
              localApi.endpoints.getClassStaff.initiate(cls.id, { forceRefetch: true })
            ).unwrap();
            const ids = new Set<string>((body.staff || []).map((s) => s.id));
            if (wantsThis) ids.add(member.userId);
            else ids.delete(member.userId);
            await setStaffOnClass({ classId: cls.id, userIds: Array.from(ids) }).unwrap();
          })()
        );
      }
      await Promise.all(ops);
      showSuccess('Class assignments updated');
      onClose();
    } catch (err: any) {
      showError(err.data?.error || err.message || 'Failed to update assignments');
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit Class Assignments"
      testId="edit-class-assignments-modal"
      maxWidth="sm"
    >
      <form onSubmit={handleSave} className="space-y-4">
        <p className="text-sm text-gray-600">
          Pick the classes{' '}
          <span className="font-medium">
            {member.firstName} {member.lastName}
          </span>{' '}
          is assigned to.
        </p>
        <div className="max-h-64 overflow-y-auto rounded-md border border-gray-300 p-2 space-y-1">
          {(classesAtLocation || []).map((cls: any) => (
            <label key={cls.id} className="flex items-center gap-2 text-sm py-0.5 cursor-pointer">
              <input
                type="checkbox"
                data-testid={`assign-class-${cls.id}`}
                checked={selectedIds.has(cls.id)}
                onChange={(e) => {
                  const next = new Set(selectedIds);
                  if (e.target.checked) next.add(cls.id);
                  else next.delete(cls.id);
                  setSelectedIds(next);
                }}
              />
              <span>{cls.name}</span>
            </label>
          ))}
        </div>
        {selectedIds.size === 0 && (
          <p className="text-xs text-amber-600">
            No classes selected — this staff won't see any students or attendance.
          </p>
        )}
        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading}
            data-testid="save-class-assignments-btn"
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-50"
          >
            {isLoading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

// ========== Edit Role Modal (location level only) ==========
const EditRoleModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  member: TeamMember | null;
  organizationId: string;
  locationId: string;
}> = ({ isOpen, onClose, member, organizationId, locationId }) => {
  const { showSuccess, showError } = useToast();
  const [newRole, setNewRole] = useState(member?.role || 'STAFF');
  const [updateRole, { isLoading }] = useUpdateLocationMemberRoleMutation();

  React.useEffect(() => {
    if (member) setNewRole(member.role);
  }, [member]);

  const roleOptions = useMemo(() => {
    return [
      { value: 'STAFF', label: 'Staff' },
      { value: 'LOCATION_ADMIN', label: 'Location Admin' },
    ];
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!member) return;
    try {
      await updateRole({
        orgId: organizationId,
        locationId,
        memberId: member.userId,
        currentRole: member.role,
        newRole,
      }).unwrap();
      showSuccess(`Role updated to ${ROLE_LABELS[newRole] || newRole}`);
      onClose();
    } catch (err: any) {
      showError(err.data?.error || 'Failed to update role');
    }
  };

  if (!member) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit Role"
      testId="edit-role-modal"
      maxWidth="sm"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <p className="text-sm text-gray-600">
          Change role for{' '}
          <span className="font-medium">
            {member.firstName} {member.lastName}
          </span>
        </p>
        <div>
          <label htmlFor="edit-role" className="block text-sm font-medium text-gray-700">
            Role
          </label>
          <select
            id="edit-role"
            data-testid="edit-role-select"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            value={newRole}
            onChange={(e) => setNewRole(e.target.value)}
          >
            {roleOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading || newRole === member.role}
            data-testid="edit-role-submit-btn"
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-50"
          >
            {isLoading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

// ========== Team Page ==========
const Team: React.FC = () => {
  const { showSuccess, showError } = useToast();
  const { user, activeRole, isAdmin } = useAuth();
  const { effectiveLocationId, effectiveOrgId, activeLocationName, activeLocationRole, isInOrg } =
    useLocationContext();

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [editMember, setEditMember] = useState<TeamMember | null>(null);
  const [assignmentsMember, setAssignmentsMember] = useState<TeamMember | null>(null);

  const callerRole = activeLocationRole || activeRole;
  const isSuperAdminTopLevel = activeRole === 'SUPER_ADMIN' && !isInOrg;
  const organizationId = effectiveOrgId || '';

  // Determine the current level
  const level: TeamLevel = isSuperAdminTopLevel
    ? 'super_admin'
    : effectiveLocationId
    ? 'location'
    : 'org';

  // Fetch data based on level
  const { data: superAdminData, isLoading: superLoading } = useGetSuperAdminsQuery(undefined, {
    skip: !isSuperAdminTopLevel,
  });
  const { data: orgData, isLoading: orgLoading } = useGetOrgMembersQuery(organizationId, {
    skip: isSuperAdminTopLevel || !!effectiveLocationId || !organizationId,
  });
  const { data: locationData, isLoading: locLoading } = useGetLocationMembersQuery(
    { orgId: organizationId, locationId: effectiveLocationId || '' },
    { skip: isSuperAdminTopLevel || !effectiveLocationId || !organizationId }
  );

  const data = isSuperAdminTopLevel ? superAdminData : effectiveLocationId ? locationData : orgData;
  const isLoading = isSuperAdminTopLevel
    ? superLoading
    : effectiveLocationId
    ? locLoading
    : orgLoading;

  // Remove mutations
  const [removeSuperAdmin] = useRemoveSuperAdminMutation();
  const [removeOrgMember] = useRemoveOrgMemberMutation();
  const [removeLocationMember] = useRemoveLocationMemberMutation();

  // Admin MFA reset — tenant-scoped, location level only (mirrors
  // removeLocationMember's id plumbing: organizationId + effectiveLocationId).
  const [mfaAdminReset] = useMfaAdminResetMutation();
  const [pendingMfaReset, setPendingMfaReset] = useState<TeamMember | null>(null);

  // Resend invite mutations
  const [resendAdminInvite] = useResendAdminInviteMutation();
  const [resendOrgMemberInvite] = useResendOrgMemberInviteMutation();
  const [resendLocationMemberInvite] = useResendLocationMemberInviteMutation();

  const [pendingRemove, setPendingRemove] = useState<TeamMember | null>(null);

  // Team management is admin-only. Placed after every hook so the rules of
  // hooks still hold on the denied path.
  //
  // The backend already refuses staff (403 on both the member read and the
  // invite write), so this was never a privilege escalation — but with no
  // guard a staff member reaching /team directly got the full page, and the
  // read 403 rendered as the empty state "No team members found / Invite
  // someone to get started". That reads as "this location has no team", which
  // is actively misleading, and the invite modal offered a Location Admin
  // role the caller could never actually grant.
  //
  // Same shape as ChecklistSignOffQueue, the sibling admin-only page.
  if (!isAdmin) {
    return (
      <div
        data-testid="team-no-access"
        className="min-h-screen flex items-center justify-center text-sm text-slate-500"
      >
        You don&apos;t have access to this page.
      </div>
    );
  }

  const members: TeamMember[] = data?.members || [];

  const canManage = (member: TeamMember): boolean => {
    if (!user) return false;
    // Pending invites can always be managed (no self-check needed since they aren't real users yet)
    if (!member.isPendingInvite && member.userId === user.id) return false;
    if (callerRole === 'LOCATION_ADMIN') {
      return member.role !== 'ORG_ADMIN' && member.role !== 'SUPER_ADMIN';
    }
    if (callerRole === 'ORG_ADMIN') {
      return member.role !== 'SUPER_ADMIN';
    }
    if (callerRole === 'SUPER_ADMIN') {
      return true;
    }
    return false;
  };

  const canEditRole = (member: TeamMember): boolean => {
    // Role editing is only available at location level
    return level === 'location' && canManage(member);
  };

  const handleRemove = (member: TeamMember) => {
    setPendingRemove(member);
  };

  const handleResendInvite = async (member: TeamMember) => {
    try {
      if (level === 'super_admin') {
        await resendAdminInvite(member.userId).unwrap();
      } else if (level === 'org') {
        await resendOrgMemberInvite({ orgId: organizationId, memberId: member.userId }).unwrap();
      } else {
        await resendLocationMemberInvite({
          orgId: organizationId,
          locationId: effectiveLocationId || '',
          memberId: member.userId,
        }).unwrap();
      }
      showSuccess(`Invitation resent to ${member.email}`);
    } catch (err: any) {
      showError(err.data?.error || 'Failed to resend invitation');
    }
  };

  const confirmRemove = async () => {
    if (!pendingRemove) return;
    const member = pendingRemove;
    setPendingRemove(null);
    try {
      if (level === 'super_admin') {
        await removeSuperAdmin({
          memberId: member.userId,
          isPendingInvite: member.isPendingInvite,
        }).unwrap();
      } else if (level === 'org') {
        await removeOrgMember({
          orgId: organizationId,
          memberId: member.userId,
          role: member.role,
          isPendingInvite: member.isPendingInvite,
        }).unwrap();
      } else {
        await removeLocationMember({
          orgId: organizationId,
          locationId: effectiveLocationId || '',
          memberId: member.userId,
          role: member.role,
          isPendingInvite: member.isPendingInvite,
        }).unwrap();
      }
      const memberDisplay = member.firstName
        ? `${member.firstName} ${member.lastName || ''}`.trim()
        : member.email;
      showSuccess(
        member.isPendingInvite
          ? `Invitation for ${memberDisplay} has been cancelled`
          : `${memberDisplay} has been removed`
      );
    } catch (err: any) {
      showError(
        err.data?.error ||
          (member.isPendingInvite ? 'Failed to cancel invitation' : 'Failed to remove team member')
      );
    }
  };

  const handleMfaReset = (member: TeamMember) => {
    setPendingMfaReset(member);
  };

  const confirmMfaReset = async () => {
    if (!pendingMfaReset) return;
    const member = pendingMfaReset;
    setPendingMfaReset(null);
    try {
      await mfaAdminReset({
        organizationId,
        locationId: effectiveLocationId || '',
        targetUserId: member.userId,
      }).unwrap();
      const memberDisplay = member.firstName
        ? `${member.firstName} ${member.lastName || ''}`.trim()
        : member.email;
      showSuccess(`Two-factor authentication has been reset for ${memberDisplay}`);
    } catch (err: any) {
      showError(err.data?.error || 'Failed to reset two-factor authentication');
    }
  };

  return (
    <div className="py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="md:flex md:items-center md:justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Team</h2>
            <p className="mt-1 text-sm text-gray-500">
              {members.length} member{members.length !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="mt-4 md:mt-0">
            <button
              onClick={() => setShowInviteModal(true)}
              data-testid="invite-member-btn"
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
            >
              <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
              Invite Member
            </button>
          </div>
        </div>

        {/* Quiet, dismissible nudge toward the compliance register — see
            StaffComplianceNudge for why this is the one place Team mentions
            absence at all. Location-scoped (the register is per-location)
            and admin-only, same reach as the register itself; a non-admin's
            query would just be refused server-side, so this also spares
            STAFF a pointless fetch. */}
        {isAdmin && level === 'location' && effectiveLocationId && (
          <StaffComplianceNudge locationId={effectiveLocationId} />
        )}

        {/* Loading */}
        {isLoading && (
          <div className="bg-white shadow rounded-lg p-8">
            <div className="animate-pulse space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-4">
                  <div className="h-10 w-10 bg-gray-200 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-1/3" />
                    <div className="h-3 bg-gray-200 rounded w-1/4" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty state */}
        {!isLoading && members.length === 0 && (
          <div className="bg-white shadow rounded-lg text-center py-12">
            <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No team members found</h3>
            <p className="mt-1 text-sm text-gray-500">Invite someone to get started.</p>
          </div>
        )}

        {/* Table */}
        {!isLoading && members.length > 0 && (
          <div className="bg-white shadow rounded-lg overflow-hidden" data-testid="team-table">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Location
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {members.map((member) => (
                    <tr
                      key={`${member.userId}-${member.role}-${member.locationId || 'org'}`}
                      data-testid="team-member-row"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                            <span className="text-xs font-medium text-primary-600">
                              {member.firstName
                                ? `${member.firstName[0]}${member.lastName?.[0] || ''}`
                                : member.email?.[0]?.toUpperCase()}
                            </span>
                          </div>
                          <div className="text-sm font-medium text-gray-900">
                            {member.firstName
                              ? `${member.firstName} ${member.lastName || ''}`.trim()
                              : member.email}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {member.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            ROLE_BADGE[member.role] || 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {ROLE_LABELS[member.role] || member.role}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {member.locationName || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            STATUS_BADGE[member.status] || 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {member.status === 'active'
                            ? 'Active'
                            : member.status === 'invited' || member.status === 'pending'
                            ? 'Pending'
                            : member.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                        <div className="flex items-center justify-end gap-2">
                          {/* Compliance link — location-level, real (non-pending)
                              members only. Outside canManage(member) on purpose:
                              viewing/adding compliance records is not a "manage
                              this person's membership" action, so it's also
                              offered on the caller's own row (self-service
                              add-credential) and on rows canManage would
                              otherwise hide (e.g. a peer admin). The page itself
                              enforces who can do what once there. */}
                          {level === 'location' && !member.isPendingInvite && (
                            <ScopedLink
                              to={`/staff/${member.userId}/compliance`}
                              data-testid="team-member-compliance-link"
                              className="text-primary-600 hover:text-primary-900 text-sm font-medium"
                            >
                              Compliance
                            </ScopedLink>
                          )}
                          {canManage(member) && (
                            <>
                              {member.isPendingInvite ? (
                                <>
                                  <button
                                    onClick={() => handleResendInvite(member)}
                                    data-testid="resend-invite-btn"
                                    className="text-primary-600 hover:text-primary-900 text-sm font-medium"
                                  >
                                    Resend Invite
                                  </button>
                                  <button
                                    onClick={() => handleRemove(member)}
                                    data-testid="cancel-invite-btn"
                                    className="text-red-600 hover:text-red-900 text-sm font-medium"
                                  >
                                    Cancel Invite
                                  </button>
                                </>
                              ) : (
                                <>
                                  {canEditRole(member) && (
                                    <button
                                      onClick={() => setEditMember(member)}
                                      data-testid="edit-role-btn"
                                      className="text-primary-600 hover:text-primary-900 text-sm font-medium"
                                    >
                                      Edit Role
                                    </button>
                                  )}
                                  {member.role === 'STAFF' &&
                                    level === 'location' &&
                                    canManage(member) && (
                                      <button
                                        onClick={() => setAssignmentsMember(member)}
                                        data-testid="edit-assignments-btn"
                                        className="text-primary-600 hover:text-primary-900 text-sm font-medium"
                                      >
                                        Edit Classes
                                      </button>
                                    )}
                                  {/* Admin MFA reset — location level only
                                      (the mfa-reset endpoint is tenant-scoped
                                      by organizationId+locationId in the
                                      path). Same canManage gate as Remove:
                                      rank-checked server-side too, but this
                                      keeps the button from ever appearing for
                                      a peer/superior or for yourself. */}
                                  {level === 'location' && (
                                    <button
                                      onClick={() => handleMfaReset(member)}
                                      data-testid={`mfa-reset-btn-${member.userId}`}
                                      className="text-primary-600 hover:text-primary-900 text-sm font-medium"
                                    >
                                      Reset 2FA
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleRemove(member)}
                                    data-testid="remove-member-btn"
                                    className="text-red-600 hover:text-red-900 text-sm font-medium"
                                  >
                                    Remove
                                  </button>
                                </>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Modals */}
        <InviteModal
          isOpen={showInviteModal}
          onClose={() => setShowInviteModal(false)}
          level={level}
          organizationId={organizationId}
          locationId={effectiveLocationId || undefined}
          currentLocationName={activeLocationName || undefined}
          callerRole={callerRole}
        />
        {level === 'location' && (
          <>
            <EditRoleModal
              isOpen={!!editMember}
              onClose={() => setEditMember(null)}
              member={editMember}
              organizationId={organizationId}
              locationId={effectiveLocationId || ''}
            />
            <EditClassAssignmentsModal
              isOpen={!!assignmentsMember}
              onClose={() => setAssignmentsMember(null)}
              member={assignmentsMember}
              locationId={effectiveLocationId || ''}
            />
          </>
        )}
        <ConfirmDialog
          isOpen={!!pendingRemove}
          onConfirm={confirmRemove}
          onCancel={() => setPendingRemove(null)}
          title={pendingRemove?.isPendingInvite ? 'Cancel Invitation' : 'Remove Team Member'}
          message={
            pendingRemove
              ? pendingRemove.isPendingInvite
                ? `Cancel the invitation for ${pendingRemove.email}?`
                : `Remove ${
                    pendingRemove.firstName
                      ? `${pendingRemove.firstName} ${pendingRemove.lastName || ''}`.trim()
                      : pendingRemove.email
                  } as ${ROLE_LABELS[pendingRemove.role] || pendingRemove.role}?`
              : ''
          }
          confirmLabel={pendingRemove?.isPendingInvite ? 'Cancel Invite' : 'Remove'}
          variant="danger"
        />
        <ConfirmDialog
          isOpen={!!pendingMfaReset}
          onConfirm={confirmMfaReset}
          onCancel={() => setPendingMfaReset(null)}
          title="Reset Two-Factor Authentication"
          message={
            pendingMfaReset
              ? `Reset two-factor authentication for ${
                  pendingMfaReset.firstName
                    ? `${pendingMfaReset.firstName} ${pendingMfaReset.lastName || ''}`.trim()
                    : pendingMfaReset.email
                }? They will need to set it up again on next login.`
              : ''
          }
          confirmLabel="Reset 2FA"
          confirmTestId="confirm-dialog-confirm"
          variant="warning"
        />
      </div>
    </div>
  );
};

export default Team;
